import { useState } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import {
  useQuickMilestones,
  useAddQuickMilestone,
  useUpdateQuickMilestone,
  useDeleteQuickMilestone,
  type QuickMilestone,
} from "@/hooks/useQuickMilestones";
import { useRoster } from "@/hooks/useRoster";
import { HEALTH_BY_KEY } from "@/lib/projectStatus";
import { cn } from "@/lib/cn";

/**
 * Quick Milestones (Brett, 2026-09-14 — v2 after correction).
 *
 * The Overview page's crude-and-quick reporting list. Deliberately
 * SEPARATE from the Plan tab: every project starts blank, and PMs
 * hand-enter each key milestone (title / owner / expected date). The
 * RYG dot opens a labeled picker (Not assessed / Green / Yellow /
 * Red) — new milestones start "not assessed" (hollow gray ring, same
 * as the board) until someone sets a color. A yellow or red milestone
 * carries a go-to-green plan edited INLINE under the row: 1-3
 * sentences of what we'll do, who owns the recovery, and when it
 * lands. Rows are drag-reorderable (grip handle, midpoint sort_order
 * — one PATCH per drop, same scheme as the board) and editable in
 * place (pencil → title/owner/date). Done rows stay visible, struck
 * through. These + project health feed the weekly C-level updates.
 */

type Health = "green" | "yellow" | "red";

const HEALTH_CHOICES: Array<{ key: Health | null; label: string }> = [
  { key: null, label: "Not assessed" },
  { key: "green", label: "Green" },
  { key: "yellow", label: "Yellow" },
  { key: "red", label: "Red" },
];

function HealthDotIcon({ health, done }: { health: Health | null; done?: boolean }) {
  if (done) return <span className="inline-block h-2.5 w-2.5 rounded-full bg-muted-strong" />;
  if (!health) {
    return (
      <span className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-inset ring-line-strong" />
    );
  }
  return (
    <span className={cn("inline-block h-2.5 w-2.5 rounded-full", HEALTH_BY_KEY[health].dotClass)} />
  );
}

function fmtDue(iso: string | null): string {
  if (!iso) return "no date";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isOverdue(m: QuickMilestone): boolean {
  if (!m.due_date || m.done) return false;
  return new Date(m.due_date + "T23:59:59") < new Date();
}

export function QuickMilestones({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuickMilestones(projectId);
  const addMilestone = useAddQuickMilestone(projectId);
  const updateMilestone = useUpdateQuickMilestone(projectId);
  const deleteMilestone = useDeleteQuickMilestone(projectId);
  const rosterQ = useRoster();

  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const [planOpenId, setPlanOpenId] = useState<number | null>(null);
  const [healthMenuId, setHealthMenuId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const roster = Array.isArray(rosterQ.data) ? rosterQ.data : [];
  const milestones = Array.isArray(data) ? data : [];

  const submit = () => {
    const t = title.trim();
    if (!t) return;
    addMilestone.mutate(
      {
        // No health sent — a new milestone starts "not assessed"
        // (hollow gray ring) until the PM sets a color. Server appends
        // it at the bottom of the manual order.
        title: t,
        owner: owner.trim() || null,
        due_date: due || null,
      },
      {
        onSuccess: () => {
          setTitle("");
          setOwner("");
          setDue("");
          setAdding(false);
        },
      },
    );
  };

  const pickHealth = (m: QuickMilestone, next: Health | null) => {
    updateMilestone.mutate({ id: m.id, health: next });
    setHealthMenuId(null);
    // Going yellow/red is the moment the go-to-green plan gets written —
    // open the inline editor right away. Green / not-assessed closes it.
    if (next === "yellow" || next === "red") setPlanOpenId(m.id);
    else setPlanOpenId((v) => (v === m.id ? null : v));
  };

  // ONE write per drop: dropped row's sort_order = midpoint of its new
  // neighbors' effective orders (display index fallback for legacy
  // NULLs). Same scheme as the board's priority_rank.
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const draggedId = Number(active.id);
    const overIdx = milestones.findIndex((m) => m.id === Number(over.id));
    if (overIdx < 0) return;
    const effective = milestones.map((m, i) => ({
      m,
      order: m.sort_order ?? i + 1,
    }));
    const without = effective.filter((r) => r.m.id !== draggedId);
    const idx = Math.min(overIdx, without.length);
    const prev = idx > 0 ? without[idx - 1].order : null;
    const next = idx < without.length ? without[idx].order : null;
    let order: number;
    if (prev != null && next != null) order = (prev + next) / 2;
    else if (next != null) order = next - 1;
    else if (prev != null) order = prev + 1;
    else order = 1;
    updateMilestone.mutate({ id: draggedId, sort_order: order });
  };

  return (
    <div data-testid="quick-milestones">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-ink">
          Milestones
        </span>
        {milestones.length > 0 && (
          <span className="text-[10px] tabular-nums text-ink-subtle">
            {milestones.filter((m) => m.done).length}/{milestones.length} done
          </span>
        )}
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-brand-navy-600 transition-colors hover:text-brand-navy-800 dark:hover:text-brand-navy-300"
          >
            <Plus className="h-3 w-3" />
            Add milestone
          </button>
        )}
      </div>

      {isLoading && (
        <div className="mt-1 text-xs text-ink-subtle">Loading milestones…</div>
      )}
      {!isLoading && milestones.length === 0 && !adding && (
        <div className="mt-1 text-xs text-ink-subtle">
          No milestones yet — the weekly update is built from these.
        </div>
      )}

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <SortableContext
          items={milestones.map((m) => m.id)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="mt-1 divide-y divide-line-soft">
            {milestones.map((m) => (
              <MilestoneRow
                key={m.id}
                m={m}
                roster={roster.map((r) => r.name)}
                planOpen={planOpenId === m.id}
                menuOpen={healthMenuId === m.id}
                editing={editingId === m.id}
                onToggleMenu={() =>
                  setHealthMenuId(healthMenuId === m.id ? null : m.id)
                }
                onPickHealth={(h) => pickHealth(m, h)}
                onTogglePlan={() =>
                  setPlanOpenId(planOpenId === m.id ? null : m.id)
                }
                onToggleDone={() =>
                  updateMilestone.mutate({ id: m.id, done: !m.done })
                }
                onStartEdit={() => setEditingId(m.id)}
                onSaveEdit={(t, o, d) => {
                  updateMilestone.mutate({
                    id: m.id,
                    title: t,
                    owner: o || null,
                    due_date: d || null,
                  });
                  setEditingId(null);
                }}
                onCancelEdit={() => setEditingId(null)}
                onSavePlan={(plan, gtgOwner, gtgDue) => {
                  updateMilestone.mutate({
                    id: m.id,
                    gtg_plan: plan.trim() || null,
                    gtg_owner: gtgOwner.trim() || null,
                    gtg_due: gtgDue || null,
                  });
                  setPlanOpenId(null);
                }}
                onDelete={() => deleteMilestone.mutate(m.id)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {adding && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") setAdding(false);
            }}
            placeholder="Milestone — what has to happen?"
            className="min-w-[220px] flex-1 rounded-md border border-line px-2 py-1 text-sm focus:border-brand-navy-400 focus:outline-none"
            aria-label="New milestone title"
          />
          <input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            list="qm-roster-names"
            placeholder="Owner"
            className="w-36 rounded-md border border-line px-2 py-1 text-sm focus:border-brand-navy-400 focus:outline-none"
            aria-label="Milestone owner"
          />
          <datalist id="qm-roster-names">
            {roster.map((r) => (
              <option key={r.name} value={r.name} />
            ))}
          </datalist>
          <input
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className="rounded-md border border-line px-2 py-1 text-sm text-ink-3 focus:border-brand-navy-400 focus:outline-none"
            aria-label="Expected date"
          />
          <button
            onClick={submit}
            disabled={!title.trim() || addMilestone.isPending}
            className="rounded-md bg-brand-navy-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-navy-700 disabled:opacity-40"
          >
            Add
          </button>
          <button
            onClick={() => setAdding(false)}
            className="text-xs text-ink-subtle hover:text-ink-3"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One sortable row
// ---------------------------------------------------------------------------

function MilestoneRow({
  m,
  roster,
  planOpen,
  menuOpen,
  editing,
  onToggleMenu,
  onPickHealth,
  onTogglePlan,
  onToggleDone,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onSavePlan,
  onDelete,
}: {
  m: QuickMilestone;
  roster: string[];
  planOpen: boolean;
  menuOpen: boolean;
  editing: boolean;
  onToggleMenu: () => void;
  onPickHealth: (h: Health | null) => void;
  onTogglePlan: () => void;
  onToggleDone: () => void;
  onStartEdit: () => void;
  onSaveEdit: (title: string, owner: string, due: string) => void;
  onCancelEdit: () => void;
  onSavePlan: (plan: string, owner: string, due: string) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: m.id });
  const health = m.health as Health | null;
  const needsPlan = !m.done && (health === "yellow" || health === "red");
  const hasPlan = !!(m.gtg_plan || m.gtg_owner || m.gtg_due);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("group py-1.5", isDragging && "z-10 opacity-70")}
    >
      <div className="flex items-center gap-2.5">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab text-ink-ghost transition-colors hover:text-ink-subtle active:cursor-grabbing group-hover:text-ink-faint"
          title="Drag to reorder"
          aria-label={`Reorder ${m.title}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <input
          type="checkbox"
          checked={m.done}
          onChange={onToggleDone}
          className="h-3.5 w-3.5 rounded border-line-strong text-brand-navy-600 dark:text-brand-navy-400 focus:ring-brand-navy-400"
          aria-label={`Mark ${m.title} ${m.done ? "not done" : "done"}`}
        />
        <span className="relative flex shrink-0 items-center">
          <button
            onClick={() => !m.done && onToggleMenu()}
            className={cn(
              "flex items-center rounded-full p-0.5 transition-shadow",
              !m.done && "cursor-pointer hover:ring-2 hover:ring-line",
            )}
            title={
              m.done
                ? "Completed"
                : `Health: ${health ? HEALTH_BY_KEY[health].label : "not assessed"} — click to set`
            }
            aria-label={`Health of ${m.title}: ${health ?? "not assessed"}`}
          >
            <HealthDotIcon health={health} done={m.done} />
          </button>
          {menuOpen && !m.done && (
            <>
              {/* click-away backdrop */}
              <button
                className="fixed inset-0 z-10 cursor-default"
                onClick={onToggleMenu}
                aria-hidden
                tabIndex={-1}
              />
              <div className="absolute left-0 top-6 z-20 w-36 rounded-lg border border-line bg-surface py-1 shadow-lg">
                {HEALTH_CHOICES.map((c) => (
                  <button
                    key={c.label}
                    onClick={() => onPickHealth(c.key)}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-raised",
                      c.key === health
                        ? "font-semibold text-ink"
                        : "text-ink-3",
                    )}
                  >
                    <HealthDotIcon health={c.key} />
                    {c.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </span>

        {editing ? (
          <RowEditor
            m={m}
            roster={roster}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
          />
        ) : (
          <>
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-sm",
                m.done
                  ? "text-ink-subtle line-through"
                  : "font-medium text-ink",
              )}
            >
              {m.title}
            </span>
            {needsPlan && (
              <button
                onClick={onTogglePlan}
                className={cn(
                  "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors",
                  health === "red"
                    ? "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 hover:bg-red-100"
                    : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-100",
                )}
              >
                {hasPlan ? "Go-to-green plan" : "+ Go-to-green plan"}
              </button>
            )}
            <span className="w-24 shrink-0 truncate text-right text-xs text-ink-muted">
              {m.owner ?? "—"}
            </span>
            <span
              className={cn(
                "w-16 shrink-0 text-right text-xs tabular-nums",
                isOverdue(m) ? "font-semibold text-red-600 dark:text-red-400" : "text-ink-muted",
              )}
            >
              {fmtDue(m.due_date)}
            </span>
            <button
              onClick={onStartEdit}
              className="shrink-0 text-ink-ghost opacity-0 transition-opacity hover:text-brand-navy-600 dark:hover:text-brand-navy-400 group-hover:opacity-100"
              title={`Edit ${m.title}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onDelete}
              className="shrink-0 text-ink-ghost opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
              title={`Delete ${m.title}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>

      {/* Inline go-to-green: read view when closed but present, editor
          when opened. Only yellow/red rows have either. */}
      {needsPlan && !planOpen && hasPlan && !editing && (
        <button
          onClick={onTogglePlan}
          className="ml-[76px] mt-1 block max-w-3xl text-left text-xs text-ink-3 hover:text-ink"
        >
          <span
            className={cn(
              "font-semibold",
              health === "red" ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300",
            )}
          >
            Go to green:
          </span>{" "}
          {m.gtg_plan}
          {(m.gtg_owner || m.gtg_due) && (
            <span className="text-ink-subtle">
              {" — "}
              {m.gtg_owner ?? "unowned"}
              {m.gtg_due ? ` · green by ${fmtDue(m.gtg_due)}` : ""}
            </span>
          )}
        </button>
      )}
      {needsPlan && planOpen && (
        <GoToGreenEditor
          milestone={m}
          rosterNames={roster}
          onSave={onSavePlan}
          onCancel={onTogglePlan}
        />
      )}
    </li>
  );
}

/** Inline title/owner/date editor — pencil opens it in place. */
function RowEditor({
  m,
  roster,
  onSave,
  onCancel,
}: {
  m: QuickMilestone;
  roster: string[];
  onSave: (title: string, owner: string, due: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(m.title);
  const [owner, setOwner] = useState(m.owner ?? "");
  const [due, setDue] = useState(m.due_date ?? "");
  const save = () => {
    if (title.trim()) onSave(title.trim(), owner.trim(), due);
  };
  return (
    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") onCancel();
        }}
        className="min-w-[180px] flex-1 rounded-md border border-line px-2 py-1 text-sm focus:border-brand-navy-400 focus:outline-none"
        aria-label={`Edit title of ${m.title}`}
      />
      <input
        value={owner}
        onChange={(e) => setOwner(e.target.value)}
        list="qm-roster-names"
        placeholder="Owner"
        className="w-32 rounded-md border border-line px-2 py-1 text-xs focus:border-brand-navy-400 focus:outline-none"
        aria-label={`Edit owner of ${m.title}`}
      />
      <input
        type="date"
        value={due}
        onChange={(e) => setDue(e.target.value)}
        className="rounded-md border border-line px-2 py-1 text-xs text-ink-3 focus:border-brand-navy-400 focus:outline-none"
        aria-label={`Edit expected date of ${m.title}`}
      />
      <button
        onClick={save}
        disabled={!title.trim()}
        className="rounded-md bg-brand-navy-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-navy-700 disabled:opacity-40"
      >
        Save
      </button>
      <button
        onClick={onCancel}
        className="text-xs text-ink-subtle hover:text-ink-3"
      >
        Cancel
      </button>
    </span>
  );
}

/** Inline go-to-green editor: the plan (1-3 sentences), who owns the
 *  recovery, and when it lands. */
function GoToGreenEditor({
  milestone,
  rosterNames,
  onSave,
  onCancel,
}: {
  milestone: QuickMilestone;
  rosterNames: string[];
  onSave: (plan: string, owner: string, due: string) => void;
  onCancel: () => void;
}) {
  const [plan, setPlan] = useState(milestone.gtg_plan ?? "");
  const [gtgOwner, setGtgOwner] = useState(milestone.gtg_owner ?? "");
  const [gtgDue, setGtgDue] = useState(milestone.gtg_due ?? "");
  return (
    <div className="ml-[76px] mt-1.5 max-w-3xl rounded-lg border border-line bg-canvas/70 p-2.5">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
        Go-to-green plan
      </div>
      <textarea
        autoFocus
        value={plan}
        onChange={(e) => setPlan(e.target.value)}
        maxLength={1000}
        rows={2}
        placeholder="1-3 sentences: what we'll do to get this back to green."
        className="w-full resize-none rounded-md border border-line bg-surface px-2 py-1.5 text-sm focus:border-brand-navy-400 focus:outline-none"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
            onSave(plan, gtgOwner, gtgDue);
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <input
          value={gtgOwner}
          onChange={(e) => setGtgOwner(e.target.value)}
          list="qm-gtg-names"
          placeholder="Who owns it"
          className="w-36 rounded-md border border-line bg-surface px-2 py-1 text-xs focus:border-brand-navy-400 focus:outline-none"
          aria-label="Go-to-green owner"
        />
        <datalist id="qm-gtg-names">
          {rosterNames.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <span className="text-[10px] text-ink-subtle">green by</span>
        <input
          type="date"
          value={gtgDue}
          onChange={(e) => setGtgDue(e.target.value)}
          className="rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink-3 focus:border-brand-navy-400 focus:outline-none"
          aria-label="Go-to-green date"
        />
        <button
          onClick={() => onSave(plan, gtgOwner, gtgDue)}
          className="rounded-md bg-brand-navy-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-navy-700"
        >
          Save
        </button>
        <button
          onClick={onCancel}
          className="text-xs text-ink-subtle hover:text-ink-3"
        >
          Cancel
        </button>
        <span className="ml-auto text-[10px] text-ink-subtle">⌘↵ saves</span>
      </div>
    </div>
  );
}
